import { Injectable, signal } from '@angular/core';
import { environment } from '../../../environments/environment';

interface DemoStatus {
  environment: string;
  reset_interval_hours: number;
  last_reset: string;
  next_reset: string;
}

/**
 * Date de la prochaine remise à zéro de l'instance de démonstration.
 *
 * La source est un fichier statique (`/demo-status.json`) écrit par le script de
 * réinitialisation, et non un endpoint applicatif : il doit rester lisible
 * PENDANT que la base est recréée (`migrate:fresh`), moment où l'API ne répond
 * plus. C'est aussi la raison pour laquelle il vit hors de la base.
 *
 * Silencieux par construction : si le fichier manque ou que la requête échoue,
 * le bandeau ne s'affiche simplement pas. Une démo sans bandeau reste utilisable ;
 * une démo bloquée par une erreur de bandeau, non.
 *
 * Volontairement en `fetch` et non via `ApiService` : cette requête ne doit
 * traverser ni l'intercepteur d'authentification ni le loader global — elle
 * part avant toute connexion et ne doit pas déclencher d'overlay.
 */
@Injectable({ providedIn: 'root' })
export class DemoStatusService {
  readonly nextReset = signal<Date | null>(null);

  /** Vrai seulement sur le build `demo` : ailleurs, le service ne fait rien. */
  readonly isDemo = environment.demo === true;

  load(): void {
    if (!this.isDemo) {
      return;
    }

    const url = `${environment.api.replace(/\/$/, '')}/demo-status.json`;

    fetch(url, { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: DemoStatus | null) => {
        const next = data?.next_reset ? new Date(data.next_reset) : null;
        if (next && !isNaN(next.getTime())) {
          this.nextReset.set(next);
        }
      })
      .catch(() => {
        /* démo sans bandeau plutôt que démo cassée */
      });
  }
}
